import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import AdminNav from "../components/AdminNav";
import { api, TOOLS, TOOL_COLOR } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, LogOut } from "lucide-react";

const Stat = ({ label, value, subline, color = "#1a1a1a", testId }) => (
  <div data-testid={testId} className="border-4 border-[#1a1a1a] rounded-3xl p-5 shadow-hard-sm bg-white">
    <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2">
      <span className="w-3 h-3 rounded-full border-2 border-[#1a1a1a]" style={{ background: color }} />
      {label}
    </div>
    <div className="font-display font-black text-3xl sm:text-4xl tabular-nums">{value}</div>
    {subline && <div className="font-mono text-[10px] uppercase tracking-wider text-[#737373] mt-1">{subline}</div>}
  </div>
);

const AdminCostos = () => {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/usage?days=${days}`);
      setUsage(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("guango_admin_token");
        navigate("/admin");
      } else {
        toast.error("No se pudo cargar el uso.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const logout = () => {
    localStorage.removeItem("guango_admin_token");
    navigate("/admin");
  };

  const total = usage?.total || {};
  const byTool = usage?.by_tool || {};
  const pricing = usage?.pricing || {};

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-24">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight">costos.</h1>
          <div className="flex gap-2 flex-wrap">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                data-testid={`costos-days-${d}`}
                onClick={() => setDays(d)}
                className={`press border-4 border-[#1a1a1a] rounded-lg px-3 py-1 font-mono text-xs uppercase font-bold shadow-hard-sm ${days === d ? "bg-[#22c55e]" : "bg-white"}`}
              >
                {d}d
              </button>
            ))}
            <button
              data-testid="costos-refresh"
              onClick={load}
              className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={3} /> recargar
            </button>
            <button
              onClick={logout}
              className="press inline-flex items-center gap-2 bg-[#ef4444] text-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
            >
              <LogOut className="w-4 h-4" strokeWidth={3} /> salir
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="generaciones" value={total.count ?? 0} testId="stat-count" />
          <Stat label="tokens entrada" value={(total.tokens_in ?? 0).toLocaleString("es")} testId="stat-tokens-in" />
          <Stat label="tokens salida" value={(total.tokens_out ?? 0).toLocaleString("es")} testId="stat-tokens-out" />
          <Stat
            label="costo total"
            value={`$${(total.cost_usd ?? 0).toFixed(4)}`}
            subline="USD"
            color="#22C55E"
            testId="stat-cost"
          />
        </div>

        <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-5 mb-6">
          <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2 text-[#737373]">precios actuales (USD por 1M tokens)</div>
          <div className="font-mono text-sm">
            entrada: <span className="font-bold">${pricing.input_per_1m_usd}</span> · salida: <span className="font-bold">${pricing.output_per_1m_usd}</span>
            <span className="text-[#737373] ml-2">— ajustables vía env vars GEMINI_PRICE_INPUT_PER_1M / GEMINI_PRICE_OUTPUT_PER_1M</span>
          </div>
        </div>

        <h2 className="font-display font-black text-2xl mb-4">por herramienta</h2>
        <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#1a1a1a] text-white">
                  <th className="text-left font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">herramienta</th>
                  <th className="text-right font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">generaciones</th>
                  <th className="text-right font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">tokens in</th>
                  <th className="text-right font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">tokens out</th>
                  <th className="text-right font-mono text-xs uppercase tracking-wider font-bold px-4 py-3">costo USD</th>
                </tr>
              </thead>
              <tbody>
                {TOOLS.map((t) => {
                  const u = byTool[t.id] || { count: 0, tokens_in: 0, tokens_out: 0, cost_usd: 0 };
                  return (
                    <tr key={t.id} className="border-t-2 border-[#1a1a1a]/15" data-testid={`row-${t.id}`}>
                      <td className="px-4 py-3">
                        <span className="inline-block font-mono text-[10px] uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-2 py-0.5" style={{ background: TOOL_COLOR[t.id] }}>
                          {t.title}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm tabular-nums text-right">{u.count}</td>
                      <td className="px-4 py-3 font-mono text-sm tabular-nums text-right">{u.tokens_in.toLocaleString("es")}</td>
                      <td className="px-4 py-3 font-mono text-sm tabular-nums text-right">{u.tokens_out.toLocaleString("es")}</td>
                      <td className="px-4 py-3 font-mono text-sm tabular-nums text-right font-bold">${u.cost_usd.toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdminCostos;
